import { SharePointListClient, odataEscape, type ListItem } from '../SharePointListClient';
import { parseMoney } from '@/shared/lib/money';

/**
 * `99.ABM_Parametros` — app-level parameters keyed by `NombreParametro_P`.
 * The row we care about on Home is `Impuesto SIRCREB`, whose `Valor_P`
 * stores the tax as a fraction (`0.008` for 0.8%) and whose
 * `FechaActualizacion_P` is a free-form timestamp string ending in
 * `dd/mm/yyyy HH:mm`. Freshness is decided by matching the last seven
 * characters (`mm/yyyy`) against the current month — same check the
 * PowerApps Home screen uses.
 */
export type ParametroFields = {
  Title?: string | null;
  NombreParametro_P?: string | null;
  Valor_P?: number | string | null;
  Descripcion_P?: string | null;
  FechaActualizacion_P?: string | null;
  /** `usuario_app` del último que lo modificó. Sólo lectura — lo estampa el
   *  servidor en cada update. NULL en filas anteriores a 2026-09-11. */
  UsuarioActualizacion_P?: string | null;
  Status_P?: string | null;
};

export type ParametroItem = ListItem<ParametroFields>;

export const parametrosClient = new SharePointListClient<ParametroFields>('ABM_Parametros');

export const SIRCREB_PARAM_NAME = 'Impuesto SIRCREB';

/**
 * Gift ("Regalo") minimum-amount parameters. A vendor can only mark a
 * cotización line as regalo once the cotización Total (con IVA) reaches
 * this minimum. `REGALO_MINIMO_PARAM` is in local currency (country-scoped
 * ARS/MXN); `REGALO_MINIMO_USD_PARAM` is used when the cotización is in USD.
 * Both seeded at '0' = feature inert (gifting always allowed) until set.
 */
export const REGALO_MINIMO_PARAM = 'Monto Minimo Regalo';
export const REGALO_MINIMO_USD_PARAM = 'Monto Minimo Regalo USD';

/**
 * Resolve the gift threshold for the cotización currency from a list of
 * parameters (use the country-scoped `listAllParametros()` as the source).
 * USD cotizaciones read the USD parameter; ARS/MXN read the local one.
 * Returns 0 when the parameter is missing/blank (= no gating).
 */
export function regaloMinimoThreshold(
  params: ParametroItem[],
  moneda: 'ARS' | 'MXN' | 'USD',
): number {
  const wanted = moneda === 'USD' ? REGALO_MINIMO_USD_PARAM : REGALO_MINIMO_PARAM;
  const row = params.find((p) => p.fields.NombreParametro_P === wanted);
  return parseMoney(row?.fields.Valor_P) ?? 0;
}

export async function getSircrebParam(): Promise<ParametroItem | null> {
  const rows = await parametrosClient.listItems({
    filter: `fields/NombreParametro_P eq ${odataEscape(SIRCREB_PARAM_NAME)}`,
    top: 1,
  });
  return rows[0] ?? null;
}

/**
 * True when the parameter's last update falls in the current month.
 * PowerApps compares `Right(FechaActualizacion_P, 7)` to
 * `Text(Today(), "mm/yyyy")` — which works on legacy date-only stamps
 * (`dd/mm/yyyy`) but breaks on React-written stamps that include an
 * `hh:mm` suffix. We extract the first `dd/mm/yyyy` token via regex
 * so both formats match.
 */
export function isParametroUpToDate(item: ParametroItem | null, now = new Date()): boolean {
  const stamp = item?.fields.FechaActualizacion_P ?? '';
  const m = stamp.match(/\b(\d{2})\/(\d{2})\/(\d{4})\b/);
  if (!m) return false;
  const mm = Number(m[2]);
  const yyyy = Number(m[3]);
  return mm === now.getMonth() + 1 && yyyy === now.getFullYear();
}

/**
 * Percentage shown to the user (e.g. stored `"0,008"` → `0.8`).
 *
 * `Valor_P` is a text column storing comma-decimal strings — PowerApps
 * wraps reads in `Value(...)` to coerce them to numbers. We go through
 * `parseMoney` which already understands both `"0,008"` and `"0.008"`.
 */
export function sircrebPercent(item: ParametroItem | null): number | null {
  const n = parseMoney(item?.fields.Valor_P);
  return n != null ? n * 100 : null;
}

/**
 * Patch the SIRCREB parameter AND back-fill any zero-monto SIRCREB
 * placeholder rows for the current month. Goes through
 * `/api/tesoreria/sircreb-update` so the rate change + backfill are
 * atomic (see the route's JSDoc for PowerApps origin).
 *
 * `percent` is the user-facing percentage (e.g. `0.8` for 0.8%); the
 * backend stores `Valor_P = percent/100` as a comma-decimal string.
 */
export async function updateSircreb(
  _itemId: string,
  percent: number,
): Promise<{ outcome: 'updated'; paramId: string; rowsBackfilled: number }> {
  const { apiFetch } = await import('../client');
  return apiFetch<{ outcome: 'updated'; paramId: string; rowsBackfilled: number }>(
    '/api/tesoreria/sircreb-update',
    { method: 'POST', body: { percent } },
  );
}

/**
 * List every parameter for the active country, sorted by name. Used by
 * the Configuración → Parámetros del Sistema CRUD. Status filter is the
 * caller's responsibility (the entity component applies it client-side).
 */
export async function listAllParametros(): Promise<ParametroItem[]> {
  return parametrosClient.listItems({
    orderBy: 'fields/NombreParametro_P asc',
    top: 5000,
  });
}

/**
 * Patch a parameter's editable fields. SIRCREB has its own dedicated
 * endpoint (`updateSircreb`, used by the Home-screen rate editor)
 * because rate changes also backfill the current month's
 * MovimientoCajas placeholders — callers editing SIRCREB through the
 * generic CRUD should be routed there instead.
 */
export type ParametroPayload = {
  valor: string;
  descripcion: string | null;
};

export async function updateParametro(
  itemId: string,
  payload: ParametroPayload,
): Promise<ParametroItem> {
  return parametrosClient.updateItem(itemId, {
    Valor_P: payload.valor,
    Descripcion_P: payload.descripcion,
  });
}
